import { requestWithAuthRetry } from '../../auth/api/authenticatedRequest';

/**
 * 后端返回的支付响应
 */
export interface CreatePaymentResponse {
    /** 内部订单 ID */
    orderId: number;
    /** 订单号 */
    orderNo: string;
    /** 支付金额 */
    amount: number;
    /** 充值点数 */
    points: number;
    /** 渠道编码 */
    channelCode: string;
    /** 支付类型: QR_CODE / REDIRECT / SDK */
    paymentType: 'QR_CODE' | 'REDIRECT' | 'SDK';
    /** 二维码链接 (扫码支付时) */
    qrCodeUrl?: string;
    /** HTML 表单 (跳转支付时) */
    form?: string;
    /** 过期时间 */
    expireAt: string;
}

/**
 * 支付状态响应
 */
export interface PaymentStatusResponse {
    orderId: number;
    orderNo: string;
    /** 订单状态: PENDING / PAID / FAILED / CLOSED / REFUNDED */
    status: 'PENDING' | 'PAID' | 'FAILED' | 'CLOSED' | 'REFUNDED';
    /** 是否已充值成功 */
    recharged: boolean;
    /** 充值点数 */
    points: number;
    /** 充值后余额 */
    balance?: number;
    /** 退款状态: NONE / PROCESSING / SUCCESS / FAILED */
    refundStatus: 'NONE' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
    /** 退款时实际扣回的点数 */
    refundPoints?: number;
}

/**
 * 套餐信息 (前端硬编码，保持向后兼容)
 */
export interface PaymentPackage {
    id: 'small' | 'medium' | 'large';
    price: string;
    originalPrice?: string;
    points: number;
    labelKey: string;
    descriptionKey: string;
    featuresKey: string;
    recommended?: boolean;
}

/**
 * 支付渠道
 */
export interface PaymentChannel {
    code: 'ALIPAY' | 'WECHAT';
    name: string;
    icon: string;
    type: 'QR_CODE' | 'REDIRECT';
    disabled?: boolean;
}

export const PAYMENT_CHANNELS: PaymentChannel[] = [
    { code: 'ALIPAY', name: '支付宝', icon: '💳', type: 'REDIRECT' },
    { code: 'WECHAT', name: '微信支付', icon: '💚', type: 'QR_CODE', disabled: true },
];

export const PAYMENT_PACKAGES: PaymentPackage[] = [
    {
        id: 'small',
        price: '9.90',
        points: 150,
        labelKey: 'payment.packages.small.title',
        descriptionKey: 'payment.packages.small.desc',
        featuresKey: 'payment.packages.small.features',
        recommended: false
    },
    {
        id: 'medium',
        price: '29.90',
        points: 600,
        labelKey: 'payment.packages.medium.title',
        descriptionKey: 'payment.packages.medium.desc',
        featuresKey: 'payment.packages.medium.features',
        recommended: true
    },
    {
        id: 'large',
        price: '49.90',
        points: 1200,
        labelKey: 'payment.packages.large.title',
        descriptionKey: 'payment.packages.large.desc',
        featuresKey: 'payment.packages.large.features',
        recommended: false
    }
];

/**
 * 创建支付订单 (支持多渠道)
 */
export async function createPayment(packageCode: string, channelCode?: string): Promise<CreatePaymentResponse> {
    // httpClient 已自动解包 ApiResponse<T>，直接返回 data 部分
    return requestWithAuthRetry<CreatePaymentResponse>(
        'POST',
        '/api/v1/payment/create',
        {
            json: { packageCode, channelCode },
            retryLabel: 'createPayment'
        }
    );
}

/**
 * 创建支付宝订单 (兼容旧接口)
 */
export async function createAlipayPayment(packageId: string): Promise<CreatePaymentResponse> {
    // httpClient 已自动解包 ApiResponse<T>，直接返回 data 部分
    return requestWithAuthRetry<CreatePaymentResponse>(
        'POST',
        '/api/v1/payment/alipay/create',
        {
            json: { packageCode: packageId },
            retryLabel: 'createAlipayPayment'
        }
    );
}

/**
 * 查询订单状态
 * @param sync 订单待支付时让后端主动向支付宝查单（同步跳回时异步通知可能尚未到达）
 */
export async function getPaymentStatus(orderNo: string, sync = false): Promise<PaymentStatusResponse> {
    // httpClient 已自动解包 ApiResponse<T>，直接返回 data 部分
    return requestWithAuthRetry<PaymentStatusResponse>(
        'GET',
        `/api/v1/payment/status/${encodeURIComponent(orderNo)}${sync ? '?sync=true' : ''}`,
        { retryLabel: 'getPaymentStatus' }
    );
}

/**
 * 轮询支付状态
 * @param orderNo 订单号
 * @param onSuccess 支付成功回调
 * @param onFailed 支付失败回调
 * @param maxAttempts 最大尝试次数
 * @param interval 轮询间隔(毫秒)
 * @returns 取消轮询函数
 */
export function pollPaymentStatus(
    orderNo: string,
    onSuccess: (response: PaymentStatusResponse) => void,
    onFailed: (response: PaymentStatusResponse) => void,
    maxAttempts = 60,
    interval = 3000
): () => void {
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
        if (cancelled || attempts >= maxAttempts) return;
        attempts++;

        try {
            const status = await getPaymentStatus(orderNo);
            
            if (status.status === 'PAID' && status.recharged) {
                onSuccess(status);
                return;
            }
            
            if (status.status === 'FAILED' || status.status === 'CLOSED' || status.status === 'REFUNDED') {
                onFailed(status);
                return;
            }

            // 继续轮询
            if (!cancelled) {
                setTimeout(poll, interval);
            }
        } catch (error) {
            console.error('Poll payment status failed:', error);
            // 网络错误继续重试
            if (!cancelled) {
                setTimeout(poll, interval);
            }
        }
    };

    // 开始轮询
    setTimeout(poll, interval);

    // 返回取消函数
    return () => {
        cancelled = true;
    };
}
